import React, { FC, useCallback, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Button } from '@wmgtech/legato'

import QuillForm from '../QuillForm/QuillForm'
import { IsaacTable, IDeleteArguments } from 'components/Project/IsaacTable'
import { onNotifySuccessIssacDelete, onNotifySuccessIssacSave } from 'modulesCommon/Forms/utils/issakUtils'
import { ProjectCreateContainer, ProjectCreateHelper, StepperNavigation } from 'modules/ProjectCreate/components'
import { IsaakLinkFormModal } from 'modulesCommon/Forms/components'
import { FormInput, FormSelect } from 'components/FormWithGroup'
import { helperInfo, rules } from 'modules/ProjectCreate/constants'
import { toolbarModules } from 'shared/quill/editorOptions'

import { IIsaacLink } from 'shared/interfaces'
import { IProjectCreationProps } from './ProjectCreation.props'
import { IProjectForm } from '../../types'

import './ProjectCreation.scss'

const ProjectCreation: FC<IProjectCreationProps> = ({ unitsOptions }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const { control, reset, getValues } = useFormContext<IProjectForm>()
  const { fields, append, remove } = useFieldArray<IProjectForm, 'isaacUrl', 'isaacUrl'>({
    control,
    name: 'isaacUrl'
  })
  const isaacLinkList = getValues('isaacUrl')

  const onIsaacLinkAdd = useCallback(() => {
    reset({ ...getValues() })
    setIsModalOpen(true)
  }, [reset, getValues])

  const handleIsaacLinkSave = useCallback(
    (data: IIsaacLink) => {
      append(data)
      onNotifySuccessIssacSave()
      setIsModalOpen(false)
    },
    [append]
  )

  const handleRowDelete = useCallback<(data: IDeleteArguments) => void>(
    ({ idx }) => {
      onNotifySuccessIssacDelete()
      remove(idx)
    },
  [remove]
  )

  return (
    <>
      <div className="create-project">
        <StepperNavigation onNext={() => {}} />
        <ProjectCreateContainer>
          <div className="create-project__container">
            <div className="create-project__info">
              <h3 className="create-project__title">Create Your Project</h3>
              <FormInput
                control={control}
                fieldName="projectName"
                label="Project"
                rules={rules.projectStepRules.projectName}
                placeholder="Project Name"
              />
              <FormSelect
                label="Unit"
                rules={rules.projectStepRules.unit}
                placeholder="Select Unit"
                fieldName="unit"
                options={unitsOptions}
                control={control}
              />
              <Button
                size="sm"
                secondaryIcon="plus-circle"
                containerStyle="outline"
                colorType="secondary-black"
                className="create-project__isaac-button"
                label="Add new ISAAC link"
                onClick={onIsaacLinkAdd}
              />
              <IsaakLinkFormModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                handleIsaacLinkSave={handleIsaacLinkSave}
                isaacLinkList={isaacLinkList}
              />
            </div>
            <ProjectCreateHelper className="create-project__helper">
              <>
                <h4>Creating your Project</h4>
                <p>{helperInfo.createProject}</p>
              </>
            </ProjectCreateHelper>
          </div>
          <IsaacTable prefix="isaacUrl" hidden={!fields.length} data={fields} onDelete={handleRowDelete} />
        </ProjectCreateContainer>
        <div className="create-project-customization">
          <section className="create-project-customization__editors">
            <QuillForm
              control={control}
              fieldName="teamInfo"
              className="create-project-customization-editor"
              label="Local Team Information"
              toolbarModules={toolbarModules.minimal}
            />
            <QuillForm
              control={control}
              fieldName="info"
              className="create-project-customization-editor"
              label="Local Project Information"
              toolbarModules={toolbarModules.minimal}
            />
          </section>
          <section className="create-project-customization__helper">
            <ProjectCreateHelper dark>
              <>
                <h4>Let's get started customizing your project for Storybox</h4>
                <p>{helperInfo.customizeProject}</p>
              </>
            </ProjectCreateHelper>
          </section>
        </div>
      </div>
    </>
  )
}

export default ProjectCreation
